import { Fragment } from 'react';
import clsx from 'clsx';
import { Icon } from '@clay-run/glaze/icon';
import { FLOW_ICONS } from '../../icons';

export type BreadcrumbCrumb = {
  label: string;
  onClick?: () => void;
};

/**
 * Type scale for every crumb. `xs` is Glaze's own `Breadcrumbs` scale
 * (`text-xs text-content-tertiary`, `@clay-run/glaze/src/breadcrumbs.tsx`) and
 * the default. `sm` is Glaze `Tab size="sm"`'s scale (`text-sm font-semibold`,
 * `@clay-run/glaze/src/tabs.tsx` `content` slot + `sm` variant) — for a
 * breadcrumb that sits in the same 48px band as a `Tabs size="sm"` row and has
 * to read as the same header (Spencer QA 2026-10-07: the chat header's
 * "Chats › {account}: {title}" beside the Flow header's Companies/People/
 * Plays/Signals). Size and weight ONLY — colour stays each crumb's own.
 */
export type BreadcrumbSize = 'xs' | 'sm';

// Whole literal strings so Tailwind v4's source scanner sees every candidate.
const SIZE_CLASS: Record<BreadcrumbSize, string> = {
  xs: 'text-xs',
  sm: 'text-sm font-semibold',
};

/**
 * Shared V2 breadcrumb — extracted verbatim (bead terra-design-v2-chat-yub.13)
 * from `shell-next/account/AccountLens.tsx`'s inline `nav`. Both the V2
 * account surface and the V2 chat header (bead .3) consume this ONE copy.
 *
 * Current-crumb model (documented per the bead's "pick one" call): the
 * **last** crumb in the array is always the current one and always carries
 * `aria-current="page"`. If it has no `onClick` it renders as a plain
 * `<span>` (AccountLens's existing behaviour — the account name isn't
 * clickable). If it DOES have an `onClick` (the chat header's 2-crumb case,
 * whose second crumb opens `ChatConversationDropdown`), it renders as a
 * `<button>` that is both clickable AND `aria-current="page"` — "last crumb
 * with onClick renders as a button with aria-current", not a separate
 * `current?: boolean` field. Every non-last crumb with an `onClick` renders
 * as a plain button (no `aria-current`); a non-last crumb with no `onClick`
 * renders as inert text (not part of AccountLens's current usage, but kept
 * safe rather than silently dropping the label).
 *
 * Truncation: only the LAST crumb ever needs it — every real caller's
 * non-last crumbs are short, static labels ("Accounts", "Chats") that are
 * never the thing overflowing. The clickable-last-crumb case (the chat
 * header's long `"{accountLabel}: {chatTitle}"` crumb, opened via `onClick`)
 * used to render through this same `shrink-0`-only branch as every other
 * clickable crumb, with no `truncate`/`min-w-0` — unlike the no-`onClick`
 * last-crumb `<span>` branch right below, which already truncates. That gap
 * let the long crumb overflow its flex container and visually overlap the
 * header's trailing buttons (2026-10-01 browser QA). Mirrors V1's working
 * pattern (`EnrollQueue/FlowChatPanel.tsx`'s account/title button:
 * `min-w-0 max-w-[220px] truncate`).
 *
 * Size: `size` (default `'xs'`) picks the type scale for every crumb — see
 * `BreadcrumbSize`. `AccountLens` takes the default; `ChatHeaderV2` passes
 * `'sm'` so its crumbs match the Flow header tabs in the same band.
 */
export function Breadcrumb({
  crumbs,
  className,
  size = 'xs',
}: {
  crumbs: BreadcrumbCrumb[];
  className?: string;
  size?: BreadcrumbSize;
}) {
  const sizeClass = SIZE_CLASS[size];
  return (
    <nav aria-label="breadcrumb" className={clsx('flex min-w-0 items-center gap-1.5', className)}>
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;
        return (
          // eslint-disable-next-line react/no-array-index-key -- crumbs are a short, static, order-stable list
          <Fragment key={`${crumb.label}-${index}`}>
            {index > 0 && (
              <Icon type={FLOW_ICONS.expand} size="xs" className="shrink-0 text-content-tertiary" aria-hidden />
            )}
            {crumb.onClick ? (
              <button
                type="button"
                onClick={crumb.onClick}
                aria-current={isLast ? 'page' : undefined}
                className={clsx(
                  sizeClass,
                  'text-content-tertiary hover:text-content-primary',
                  isLast ? 'min-w-0 max-w-[220px] truncate' : 'shrink-0',
                )}
              >
                {crumb.label}
              </button>
            ) : isLast ? (
              <span aria-current="page" className={clsx(sizeClass, 'truncate text-content-primary')}>
                {crumb.label}
              </span>
            ) : (
              <span className={clsx(sizeClass, 'shrink-0 text-content-tertiary')}>{crumb.label}</span>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}
